import React, { FC, Fragment, useState } from 'react';
import { Box, Grid, FormControl, Button, InputAdornment, Alert, Link, Typography, Stack, Chip } from '@mui/material';
import { ActionPriceList } from 'models/action';
import { useTranslation } from 'react-i18next';
import NumberOnlyTextField from 'components/number-only-text-field/number-only-text-field';
import PriceListSelectorDialog, {
    PriceListOption,
} from '../../price-list-selector-dialog/price-list-selector-dialog.component';

type ActionListPageActionsProps = {
    actionPriceList: ActionPriceList;
    isUsingDefault: boolean;
    onSelectPriceList: (actionList: ActionPriceList) => void;
    onUseDefault: () => void;
    onGeneralDiscountChange: (generalDiscount: string) => void;
    actionPriceLists: ActionPriceList[];
    defaultActionPriceListId: string;
};

const ActionListPageActions: FC<ActionListPageActionsProps> = ({
    actionPriceList,
    isUsingDefault,
    onSelectPriceList,
    onUseDefault,
    onGeneralDiscountChange,
    actionPriceLists,
    defaultActionPriceListId,
}: ActionListPageActionsProps) => {
    const { t: translate } = useTranslation(['common', 'client-directory', 'lab-settings']);
    const [isSelectorOpen, setIsSelectorOpen] = useState(false);

    const priceListOptions: PriceListOption[] = actionPriceLists.map((priceList) => ({
        _id: priceList._id || '',
        name: priceList.actionPriceListName,
        currency: priceList.currency,
        isStandardPriceList: priceList.isStandardPriceList,
    }));

    const handleSelect = (priceListId: string) => {
        const selectedPriceList = actionPriceLists.find((priceList) => priceList._id === priceListId);
        if (selectedPriceList) {
            onSelectPriceList(selectedPriceList);
        }
        setIsSelectorOpen(false);
    };

    if (!actionPriceLists.length) {
        return (
            <Box>
                <Alert severity="info">{translate('client-directory:NO_CREATED_ACTION_LISTS_INFO')}</Alert>
            </Box>
        );
    }

    return (
        <Fragment>
            {!defaultActionPriceListId && (
                <Box mb={1}>
                    <Alert severity="info">
                        {translate('client-directory:NO_DEFAULT_ACTION_LIST')}
                        &nbsp;
                        <Link href={`/lab-settings/action-settings`}>{translate('lab-settings:SHOW_SETTINGS')}</Link>
                    </Alert>
                </Box>
            )}
            {isUsingDefault ? (
                <Box>
                    <Alert severity="info">{translate('client-directory:CLIENT_USES_DEFAULT_PRICE_LIST')}</Alert>
                    <Box mt={1}>
                        <Button variant="contained" onClick={() => setIsSelectorOpen(true)}>
                            {translate('client-directory:SELECT_PRICE_LIST')}
                        </Button>
                    </Box>
                </Box>
            ) : (
                <Fragment>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems={{ sm: 'center' }}
                        justifyContent="space-between"
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                {translate('client-directory:PRICE_LIST')}:
                            </Typography>
                            <Chip
                                label={
                                    actionPriceList._id === defaultActionPriceListId
                                        ? `${actionPriceList.actionPriceListName} (${translate(
                                              'common:DEFAULT',
                                          ).toLowerCase()})`
                                        : actionPriceList.actionPriceListName
                                }
                                color="primary"
                                size="medium"
                            />
                        </Stack>
                        <Stack direction="row" spacing={1}>
                            <Button onClick={() => setIsSelectorOpen(true)}>
                                {translate('client-directory:CHANGE_PRICE_LIST')}
                            </Button>
                            <Button color="secondary" onClick={onUseDefault}>
                                {translate('client-directory:USE_DEFAULT_PRICE_LIST')}
                            </Button>
                        </Stack>
                    </Stack>
                    <Grid container spacing={1} mt={0.5}>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth>
                                <NumberOnlyTextField
                                    id="general-discount"
                                    value={actionPriceList.generalDiscount || ''}
                                    label={translate('client-directory:GENERAL_DISCOUNT')}
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                        inputProps: { min: 0, max: 100 },
                                    }}
                                    onValueChange={onGeneralDiscountChange}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                </Fragment>
            )}
            {isSelectorOpen && (
                <PriceListSelectorDialog
                    open={isSelectorOpen}
                    title={translate('client-directory:SELECT_PRICE_LIST')}
                    options={priceListOptions}
                    selectedId={actionPriceList._id}
                    onSelect={handleSelect}
                    onClose={() => setIsSelectorOpen(false)}
                />
            )}
        </Fragment>
    );
};

export default ActionListPageActions;
